import { useHandler } from "@/hooks";
import { cn } from "@/lib/utils";
import * as Tabs from "@radix-ui/react-tabs";
import { useState } from "react";
import buildImage from "./build.webp?url";
import connectImage from "./connect.webp?url";
import debugImage from "./debug.webp?url";
import deployImage from "./deploy.webp?url";

const TAB_ITEMS = [
  { value: "build", label: "Build", image: buildImage },
  { value: "deploy", label: "Deploy", image: deployImage },
  { value: "connect", label: "Connect", image: connectImage },
  { value: "debug", label: "Debug", image: debugImage },
] as const;

type TabValue = (typeof TAB_ITEMS)[number]["value"];

export function HomeTabs() {
  const [currentTab, setCurrentTab] = useState<TabValue>("build");

  const setNextTab = useHandler((nextTab?: TabValue) => {
    setCurrentTab((previousTab) => {
      const i = TAB_ITEMS.findIndex((item) => item.value === previousTab);
      const nextComputedTab = TAB_ITEMS[(i + 1) % TAB_ITEMS.length].value;
      return nextTab ?? nextComputedTab;
    });
  });

  const handleValueChange = useHandler((value: string) => {
    if (!isValidTabValue(value)) {
      return;
    }
    setNextTab(value);
  });

  return (
    <div className="max-lg:row-start-1 h-full w-full max-w-lg space-y-10 lg:flex lg:flex-col lg:justify-between">
      <header className="space-y-3">
        <h1 className="text-4xl font-bold text-pretty leading-snug">
          Build, Deploy, and Debug MCP Servers
        </h1>
        <p className="text-muted-foreground text-lg">
          With Fiberplane’s chat-driven codegen, go from idea to running MCP
          server in minutes.
        </p>
      </header>

      <Tabs.Root
        value={currentTab}
        onValueChange={handleValueChange}
        className="space-y-4 [&:has([role=tabpanel]:hover)_[data-state=active]_span]:[animation-play-state:paused]"
      >
        <Tabs.List loop className="grid grid-cols-4 gap-4">
          {TAB_ITEMS.map(({ value, label }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="group grid gap-2 items-center"
            >
              <h2 className="text-lg md:text-xl leading-4 text-muted-foreground group-data-[state=active]:text-shimmer">
                {label}
              </h2>
              <span className="col-span-full relative overflow-hidden bg-secondary h-0.5 rounded-full">
                <span
                  className={cn(
                    "absolute inset-y-0 left-0 h-full aspect-square bg-secondary rounded-full",
                    "group-data-[state=active]:animate-grow-width group-data-[state=active]:bg-primary",
                  )}
                  onAnimationEnd={() => setNextTab()}
                />
              </span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {TAB_ITEMS.map(({ value, image, label }) => (
          <Tabs.Content key={value} value={value}>
            <div className="bg-secondary rounded-2xl overflow-hidden border relative aspect-video after:content-[''] after:absolute after:inset-0 after:rounded-2xl after:pointer-events-none after:z-10 after:[box-shadow:_inset_-10px_-10px_10px_hsl(var(--background))]">
              <img
                src={image}
                alt={label}
                className="w-full h-full object-cover object-top animate-slide-up border rounded-tl-xl translate-y-2 translate-x-2"
              />
            </div>
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </div>
  );
}

function isValidTabValue(value: string): value is TabValue {
  return TAB_ITEMS.some((item) => item.value === value);
}
